import { useSelector } from '@tanstack/react-form';
import { shallow } from '@tanstack/react-store';
import { useTranslation } from 'react-i18next';

import type { CardSides, OutputFormat } from '../../../api/cards';
import { useCardTemplates } from '../../../api/cards';
import { useIseeEnabled, useIseeSessions } from '../../../api/isee';
import { withForm } from '../../../form-hook';
import { formProps } from '../../../utils/form-props';
import { ErrorMessage } from '../../common/ErrorMessage';
import type { CardsSearchPatch } from '../cards-url-state';
import { FormFieldsInput } from '../FormFieldsInput';
import { cleanQuery } from '../isee-filters';
import type { CsvPreview, TemplateMeta } from './card-batch';
import { deriveBatch, effectiveSortBy } from './card-batch';
import { FieldsPreview } from './FieldsPreview';
import { SegmentedChoice } from './SegmentedChoice';
import { SortByChoice } from './SortByChoice';
import { cardGenFormOpts } from './use-card-batch';

/**
 * Step 3 — how the batch renders, and the values only the operator can supply.
 * Sides come first: the choice decides which fields the batch actually needs,
 * so it has to be made before they are asked for.
 */
export const OptionsStep = withForm({
  ...cardGenFormOpts,
  props: formProps<{
    /** What the chosen template's SVGs carry (vars + defaults). */
    template: TemplateMeta;
    /** Parsed CSV preview, null until one is loaded. */
    csv: CsvPreview | null;
    error: string | null;
    onSearchPatch?: (patch: CardsSearchPatch) => void;
  }>(),
  render: function OptionsStep({ form, template, csv, error, onSearchPatch }) {
    const { t } = useTranslation();
    const savedTemplates = useCardTemplates();
    const sides = useSelector(form.store, (state) => state.values.sides);
    const outputFormat = useSelector(
      form.store,
      (state) => state.values.outputFormat,
    );
    const sortBy = useSelector(form.store, (state) => state.values.sortBy);
    const formValues = useSelector(form.store, (state) => state.values.values);
    // The rest of what deriveBatch reads, in one subscription. Its selector
    // returns a fresh object, so it compares shallowly — with the default
    // `===` it would re-render this step on every store notification.
    const picks = useSelector(
      form.store,
      (state) => ({
        templateId: state.values.templateId,
        templateFile: state.values.templateFile,
        csvFile: state.values.csvFile,
        iseeQuery: state.values.iseeQuery,
        logins: state.values.logins,
      }),
      { compare: shallow },
    );
    // Every input comes from a subscription above, so this recomputes exactly
    // when it could change — a memo would key on the same things it reads.
    // Cleaned as the picker shows it — see CardGenerator.
    const iseeTree = useIseeSessions(useIseeEnabled()).tree;
    const d = deriveBatch(
      {
        ...picks,
        iseeQuery: cleanQuery(iseeTree, picks.iseeQuery),
        sides,
        values: formValues,
      },
      template,
      savedTemplates,
      csv,
    );
    const source = d.source;
    const hasBackSide = !!d.selectedTpl?.backSide;
    return (
      <>
        {/* Sides come before the field inputs — the choice decides which
        fields the batch actually needs. */}
        {hasBackSide && (
          <SegmentedChoice
            label={t('cardGenerator.sides.label')}
            options={(['both', 'front', 'back'] satisfies CardSides[]).map(
              (s) => ({ value: s, label: t(`cardGenerator.sides.${s}`) }),
            )}
            active={sides}
            onPick={(s) => {
              form.setFieldValue('sides', s);
              // Keep the URL clean: omit the default choice.
              onSearchPatch?.({ sides: s === 'front' ? undefined : s });
            }}
          />
        )}

        {(source === 'isee' || source === 'lucca') &&
          d.formFields.length > 0 && (
            <FormFieldsInput
              fields={d.formFields}
              values={formValues}
              onChange={(name, value) => {
                const next = { ...formValues, [name]: value };
                form.setFieldValue('values', next);
                onSearchPatch?.({
                  values: Object.values(next).some((v) => v !== '')
                    ? next
                    : undefined,
                });
              }}
              title={t('cardGenerator.formFields.title')}
              hint={t('cardGenerator.formFields.hint')}
            />
          )}

        {d.fieldStatuses && (
          <FieldsPreview
            statuses={d.fieldStatuses}
            csvColumns={csv?.columns ?? []}
          />
        )}

        <SegmentedChoice
          label={t('cardGenerator.outputFormat')}
          options={(['pdf', 'zip'] satisfies OutputFormat[]).map((f) => ({
            value: f,
            label: f.toUpperCase(),
          }))}
          active={outputFormat}
          onPick={(f) => {
            form.setFieldValue('outputFormat', f);
            // Keep the URL clean: omit the default format.
            onSearchPatch?.({ format: f === 'pdf' ? undefined : f });
          }}
        />

        {/* The fields on offer are the template's own, so with no template
        `sortFields` is empty and the picker renders disabled. */}
        <SortByChoice
          fields={d.sortFields}
          hasTemplate={d.hasTemplate}
          value={effectiveSortBy(sortBy, d.sortFields)}
          onPick={(field) => {
            form.setFieldValue('sortBy', field);
            // Keep the URL clean: omit the default (no ordering).
            onSearchPatch?.({ sortBy: field || undefined });
          }}
        />

        {error && <ErrorMessage message={error} />}

        <button
          type="submit"
          disabled={!d.canSubmit}
          className="btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-40"
        >
          {d.hasMissing
            ? t('cardGenerator.fixMissing')
            : t('cardGenerator.generate')}
        </button>
      </>
    );
  },
});
